import request from "@/lib/api/handler";

import { useAuth0 } from "@auth0/auth0-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useProfileQuery } from "@/queries/user/profile";

import { QueryKeys } from "@/queries/utils";
import { Candidate } from "@/queries/candidate/candidate";
import { IExperience } from "@/redux/features/candidateProfileSlice";
import { toast } from "sonner";

import { CandidateExperienceFormSchema } from "@/components/forms/candidate/add-update-experience/schema";
import localDateToDateString from "@/utils/localDateToDateString";

export const sortExperiences = (a: IExperience, b: IExperience): number => {
  if (!a.endDate && b.endDate) {
    return -1;
  } else if (a.endDate && !b.endDate) {
    return 1;
  } else if ((a.endDate ?? "") < (b.endDate ?? "")) {
    return 1;
  } else if ((a.endDate ?? "") > (b.endDate ?? "")) {
    return -1;
  } else if ((a.startDate ?? "") < (b.startDate ?? "")) {
    return 1;
  } else if ((a.startDate ?? "") > (b.startDate ?? "")) {
    return -1;
  }

  return 0;
};

export const useAddUpdateExperience = (
  profileId: string,
  onSuccess?: (v: IExperience) => void,
) => {
  const qc = useQueryClient();
  const { getAccessTokenSilently } = useAuth0();

  const { profile } = useProfileQuery();

  const { mutate, isPending, error } = useMutation({
    mutationFn: async ({
      data,
      experienceId,
    }: {
      data: CandidateExperienceFormSchema;
      experienceId?: string;
    }) => {
      const token = await getAccessTokenSilently();
      const body = buildBody(data);

      return request<IExperience>({
        method: experienceId ? "PATCH" : "POST",
        path: `/api/candidate/${profileId || "foo"}/experience/${experienceId ?? ""}`,
        token,
        body,
        contentType: "application/x-www-form-urlencoded",
      });
    },
    onSuccess: (data, { experienceId }) => {
      // Update experience for candidate
      const prevCandidate = qc.getQueryData<Candidate>(
        QueryKeys.candidate(profileId),
      );

      if (prevCandidate) {
        const updatedCandidate = {
          ...prevCandidate,
        };
        if (experienceId) {
          const experiences = [...prevCandidate.experiences];
          const prevExperienceIdx = experiences.findIndex(
            ({ id }) => id === experienceId,
          );

          if (prevExperienceIdx !== -1) {
            experiences[prevExperienceIdx] = data;
          }
          updatedCandidate.experiences = experiences;
        } else {
          updatedCandidate.experiences = [...prevCandidate.experiences, data];
        }

        updatedCandidate.experiences.sort(sortExperiences);

        qc.setQueryData<Candidate>(
          QueryKeys.candidate(profileId),
          updatedCandidate,
        );
      }

      // Update experience
      if (profile?.id === profileId) {
        const prevSelf = qc.getQueryData<Candidate>(QueryKeys.myCandidate);
        if (prevSelf) {
          const updatedCandidate = {
            ...prevSelf,
          };
          if (experienceId) {
            const experiences = [...prevSelf.experiences];
            const prevExperienceIdx = experiences.findIndex(
              ({ id }) => id === experienceId,
            );

            if (prevExperienceIdx !== -1) {
              experiences[prevExperienceIdx] = data;
            }
            updatedCandidate.experiences = experiences;
          } else {
            updatedCandidate.experiences = [...prevSelf.experiences, data];
          }

          updatedCandidate.experiences.sort(sortExperiences);

          qc.setQueryData<Candidate>(QueryKeys.myCandidate, updatedCandidate);
        }
      }

      onSuccess?.(data);
    },
    onError: () => {
      toast.error("Failed to save experience, please try again later.");
    },
  });

  return {
    updateExperience: (payload: {
      data: CandidateExperienceFormSchema;
      experienceId: string;
    }) => mutate(payload),
    addExperience: (payload: { data: CandidateExperienceFormSchema }) =>
      mutate(payload),
    isUpdatingExperience: isPending,
    updateExperienceError: error,
  };
};

function buildBody(data: CandidateExperienceFormSchema) {
  const body = new FormData();

  body.append("companyName", data.companyName);
  body.append("jobTitle", data.jobTitle);

  if (data.companyDescription) {
    body.append("companyDescription", data.companyDescription);
  }

  if (data.otherIndustries) {
    body.append("otherIndustries", data.otherIndustries);
  }

  if (data.engagementType) {
    body.append("engagementType", data.engagementType);
  }

  data.functions?.forEach((func) => body.append("functions[]", func));

  if (data.location) {
    body.append("location", data.location);
  }

  body.append(
    "startDate",
    data.startDate ? localDateToDateString(data.startDate) : "",
  );

  body.append(
    "endDate",
    data.endDate ? localDateToDateString(data.endDate) : "",
  );

  if (data.outcomes) {
    body.append("outcomes", data.outcomes);
  }

  if (data.industries && data.industries !== "Other") {
    body.append("industries", data.industries);
  }

  if (data.stage) {
    body.append("stage", data.stage);
  }

  if (data.companyLogo) {
    body.append("companyLogo", data.companyLogo);
  }

  return body;
}
